export type TutorialLink = {
  label: string;
  href: string;
  description?: string;
};

export type TutorialImage = {
  src: string;
  alt: string;
  caption: string;
};

export type TutorialModule = {
  id: string;
  title: string;
  eyebrow: string;
  serviceKey: string;
  category: string;
  summary: string;
  level: string;
  estimatedTime: string;
  takeaways: string[];
  appLinks: TutorialLink[];
  documentLinks: TutorialLink[];
  images: TutorialImage[];
  markdown: string;
};

export type HelpSection = {
  id: string;
  title: string;
  summary: string;
  image: TutorialImage;
  appLinks: TutorialLink[];
  lessons: Array<{
    title: string;
    body: string;
  }>;
  checklist: string[];
};

const screenshot = (fileName: string) => `/tutorial/assets/screenshots/${fileName}`;
const doc = (fileName: string) => `/tutorial/docs/${fileName}`;

export const TUTORIAL_RESOURCES: TutorialLink[] = [
  {
    label: "Full user course PDF",
    href: "/tutorial/valpha-user-course.pdf",
    description: "End-to-end product tutorial with the captured screenshots.",
  },
  {
    label: "AI system, agents, and trading PDF",
    href: "/tutorial/valpha-ai-system-agent-trading-tutorial.pdf",
    description: "Deep course focused on AI systems, experts, agents, and trading automation.",
  },
  {
    label: "AI tutorial HTML",
    href: "/tutorial/valpha-ai-system-agent-trading-tutorial.html",
    description: "Browser-readable version of the deep AI tutorial.",
  },
  {
    label: "Farsi course PDF",
    href: "/tutorial/farsi/valpha-farsi-ai-trading-course.pdf",
    description: "Farsi version focused on LLMs, agents, workflows, systems, and trading automation.",
  },
  {
    label: "Farsi course HTML",
    href: "/tutorial/farsi/valpha-farsi-ai-trading-course.html",
    description: "Browser-readable Farsi course.",
  },
];

export const TUTORIAL_MODULES: TutorialModule[] = [
  {
    id: "ai-foundations",
    title: "LLMs, Agents, Workflows, and Systems",
    eyebrow: "Foundation",
    serviceKey: "valpha_tutorial_ai_foundations",
    category: "ai",
    summary:
      "Build the mental model for what an LLM does, what an agent adds, and how Valpha turns them into trading workflows.",
    level: "Core",
    estimatedTime: "35 min",
    takeaways: [
      "Separate prediction, reasoning, tool use, memory, and execution instead of treating every AI feature as a chatbot.",
      "Understand why AI systems are deterministic workflows with AI experts inside them, while agents are autonomous operators with tools.",
      "Know where humans should stay in the loop before moving from analysis to live execution.",
    ],
    appLinks: [
      { label: "AI Hub", href: "/ai" },
      { label: "Automation Workspace", href: "/ai/automation" },
    ],
    documentLinks: [
      { label: "Course map", href: doc("00-course-map.md") },
      { label: "AI concepts", href: doc("14-ai-concepts-for-valpha-users.md") },
      { label: "LLM/agent/system masterclass", href: doc("21-llm-agent-workflow-system-masterclass.md") },
      { label: "Farsi foundations", href: "/tutorial/docs/farsi/01-llm-agent-workflow-system-fa.md" },
    ],
    images: [
      {
        src: screenshot("04-ai-hub.png"),
        alt: "Valpha AI Hub",
        caption: "AI Hub is the entry point for systems, agents, Model Lab, analytics, and shared AI assets.",
      },
      {
        src: screenshot("05-ai-automation-hub.png"),
        alt: "Valpha automation workspace",
        caption: "Automation separates core execution workflows from reusable assets such as prompts, presets, storage, and experts.",
      },
    ],
    markdown: `## The mental model

An LLM is a language model that predicts useful text from context. It can reason over instructions, summarize information, draft plans, compare scenarios, and produce structured outputs, but it does not automatically know your current account state, market data, internal rules, or execution permissions.

An agent wraps an LLM in an operating loop. The loop gives it a goal, available tools, memory, validators, and stopping rules. That lets the agent inspect state, call tools, revise a plan, and produce an answer or action proposal.

A workflow is a designed sequence. It is usually more controlled than an agent: collect inputs, run experts, check conditions, produce an output, and optionally trigger execution.

An AI system in Valpha is a trading workflow. It can use experts, market inputs, output schemas, schedules, backtests, shared storage, and validation. You use it when you want repeatable decision logic.

An agent in Valpha is an autonomous assistant. You use it when the job needs exploration, tool selection, memory, multi-step investigation, or delegation to sub-agents.

## Why this matters in trading

Trading automation fails when analysis, decision, sizing, and execution are blurred together. A reliable AI workflow should make each boundary explicit: what data was used, what the expert inferred, what schema was produced, what risk rule was checked, and what action was allowed.

Start with advisory outputs. Move to automation only after the same system has been backtested, forward-tested, reviewed under different market regimes, and constrained by account and risk limits.`,
  },
  {
    id: "ai-systems",
    title: "AI System Anatomy",
    eyebrow: "AI System",
    serviceKey: "valpha_tutorial_ai_system_anatomy",
    category: "ai",
    summary:
      "Learn how AI systems combine experts, inputs, schemas, caching, schedules, backtests, and validation into a repeatable trading workflow.",
    level: "Deep",
    estimatedTime: "55 min",
    takeaways: [
      "Design systems as explicit pipelines: inputs, expert reasoning, schema, validation, storage, and execution review.",
      "Use output schemas to make AI decisions auditable and machine-readable.",
      "Use schedules and backtests as separate validation modes, not substitutes for live risk controls.",
    ],
    appLinks: [
      { label: "AI Systems", href: "/ai/systems" },
      { label: "Create AI System", href: "/ai/systems" },
      { label: "AI Analytics", href: "/ai/analytics" },
    ],
    documentLinks: [
      { label: "AI system concepts", href: doc("02-ai-system-concepts.md") },
      { label: "Create an AI system", href: doc("03-create-ai-system.md") },
      { label: "Build playbooks", href: doc("16-ai-system-build-playbooks.md") },
      { label: "Scenario playbooks", href: doc("25-valpha-scenario-playbooks.md") },
    ],
    images: [
      {
        src: screenshot("06-ai-systems-list.png"),
        alt: "AI systems list",
        caption: "The list is where you manage existing systems and inspect status before opening the builder.",
      },
      {
        src: screenshot("07-ai-system-create-wizard.png"),
        alt: "AI system create wizard",
        caption: "The wizard captures purpose, mode, model choices, and the first version of the workflow.",
      },
      {
        src: screenshot("08-ai-system-builder.png"),
        alt: "AI system builder",
        caption: "The builder is the main control surface for experts, prompts, shared data, schedules, validation, and runs.",
      },
    ],
    markdown: `## Purpose

An AI system should answer one trading question consistently. Good examples are "should this symbol be allowed for a momentum setup?", "does this macro event invalidate the plan?", or "which account-safe action should be proposed after this signal?".

Avoid building one giant system that tries to research, trade, manage risk, and explain performance at the same time. Split those responsibilities into experts, validators, and downstream actions.

## Core parts

Inputs provide context: price data, indicators, account state, prompt snippets, storage assets, news, or manually supplied values.

Experts transform context into judgment. A prompt expert reasons with a model. A code expert performs deterministic logic. A loop or evaluator expert can repeatedly check conditions or compare candidate outputs.

The output schema turns model text into a contract. Instead of asking for a paragraph, define fields such as decision, confidence, setup_type, invalidation_level, risk_notes, and execution_allowed.

Caching stores reusable expensive outputs. Use it for stable context or repeated analysis, not for rapidly changing prices, balances, or execution state.

Schedules and manual runs are operating modes. Backtests are validation tools. Live execution should remain guarded by account, approval, risk, and subscription controls.`,
  },
  {
    id: "expert-builder",
    title: "Experts, Inputs, Outputs, and Caching",
    eyebrow: "Expert Builder",
    serviceKey: "valpha_tutorial_expert_builder_inputs_outputs",
    category: "ai",
    summary:
      "Go deep on expert types, input definitions, price and indicator inputs, condition evaluators, output schemas, and cache behavior.",
    level: "Deep",
    estimatedTime: "70 min",
    takeaways: [
      "Choose the expert type based on the job: reasoning, deterministic code, repeated evaluation, or condition checks.",
      "Treat every input as a typed dependency with clear freshness and failure expectations.",
      "Design schemas before prompts so the expert knows exactly what decision object it must produce.",
    ],
    appLinks: [
      { label: "Expert Library", href: "/ai/experts" },
      { label: "Prompt Snippets", href: "/ai/snippets" },
      { label: "Indicator Presets", href: "/ai/indicator-sets" },
    ],
    documentLinks: [
      { label: "Builder workflow", href: doc("04-builder-workflow-ai-manager.md") },
      { label: "AI system types and scenarios", href: doc("15-ai-system-types-and-scenarios.md") },
      { label: "Best practices and operations", href: doc("17-ai-system-best-practices-and-operations.md") },
      { label: "Expert/input/schema cookbook", href: doc("22-expert-input-schema-cache-cookbook.md") },
      { label: "Templates and schemas", href: doc("26-templates-schemas-and-review-checklists.md") },
    ],
    images: [
      {
        src: screenshot("45-expert-editor-basics-model-prompts.png"),
        alt: "Expert editor basics",
        caption: "The expert editor controls the expert type, model, prompt, and the expert-specific settings.",
      },
      {
        src: screenshot("48-expert-input-type-picker.png"),
        alt: "Expert input type picker",
        caption: "Typed inputs keep market data, indicators, prompt values, storage, and manual values explicit.",
      },
      {
        src: screenshot("49-expert-input-price-data-fields.png"),
        alt: "Expert price data input fields",
        caption: "Price inputs should define symbol, timeframe, bar count, and fallback behavior clearly.",
      },
      {
        src: screenshot("50-expert-input-indicator-fields.png"),
        alt: "Expert indicator input fields",
        caption: "Indicator inputs should specify calculation source and the exact values the expert may use.",
      },
      {
        src: screenshot("52-expert-cache-output-schema-fields.png"),
        alt: "Expert cache and output schema fields",
        caption: "Cache policy and output schema make the expert easier to reuse and audit.",
      },
      {
        src: screenshot("55-expert-output-schema-editor-fields.png"),
        alt: "Expert output schema editor",
        caption: "Schema fields define the exact response object downstream validators and automation can consume.",
      },
    ],
    markdown: `## Expert types

Use a prompt expert when the work requires interpretation: summarizing market context, comparing scenarios, explaining a signal, or producing a structured decision.

Use a code expert when the work must be deterministic: calculating levels, enforcing numeric rules, transforming fields, or rejecting invalid data before it reaches a model.

Use a loop or evaluator expert when the decision requires repeated assessment, candidate comparison, or condition checks across several inputs.

## Input design

Every input should answer four questions: what is the source, how fresh must it be, what happens if it is missing, and how will the expert reference it?

Price data inputs are best for candle context, trend, volatility, support/resistance, and session behavior. Indicator inputs are best for computed signals such as RSI, moving averages, ATR, or custom preset bundles. Prompt inputs are useful for reusable instructions. Storage inputs are useful for shared playbooks, symbol notes, policy text, and research artifacts.

## Output schema

Create the schema before refining the prompt. A trading expert usually needs fields for decision, confidence, setup_type, evidence, invalidation, risk_notes, and action_allowed. This keeps downstream automation from parsing free text.

## Caching

Cache stable work: static policy interpretation, a reusable sector thesis, or expensive slow research. Do not cache fast-moving account state, latest prices, or anything that directly decides live execution without a freshness check.`,
  },
  {
    id: "agents",
    title: "Agents, Tools, Memory, and Validation",
    eyebrow: "Agents",
    serviceKey: "valpha_tutorial_agents_tools_memory",
    category: "ai",
    summary:
      "Understand what agents are, how tools and memory change the operating model, and how to design agents that can assist trading safely.",
    level: "Deep",
    estimatedTime: "60 min",
    takeaways: [
      "Use agents for exploratory, multi-step work where the next action depends on what the previous tool call found.",
      "Expose only the tools the agent needs for the job and validate tool outputs before acting on them.",
      "Separate durable memory from same-run notes so the agent does not over-learn from temporary noise.",
    ],
    appLinks: [
      { label: "Agents", href: "/ai/agents" },
      { label: "Agent Memory", href: "/ai/agent-memory" },
      { label: "Agent Schedules", href: "/ai/schedules" },
    ],
    documentLinks: [
      { label: "Agent fundamentals", href: doc("07-agent-fundamentals.md") },
      { label: "Tools, memory, validation", href: doc("08-agent-tools-memory-validation.md") },
      { label: "Agent deep dive", href: doc("18-agent-deep-dive-concepts.md") },
      { label: "Agent architecture masterclass", href: doc("23-agent-architecture-memory-tooling-masterclass.md") },
    ],
    images: [
      {
        src: screenshot("19-agents-list.png"),
        alt: "Agents list",
        caption: "The agents list is where you manage autonomous assistants and inspect their current configuration.",
      },
      {
        src: screenshot("22-agent-tools.png"),
        alt: "Agent tools",
        caption: "Tools define what an agent is allowed to inspect, calculate, retrieve, or operate.",
      },
      {
        src: screenshot("26-agent-memory-tab.png"),
        alt: "Agent memory tab",
        caption: "Memory controls what survives beyond a single run and what remains temporary.",
      },
      {
        src: screenshot("30-agent-chat.png"),
        alt: "Agent chat",
        caption: "Agent chat is useful for guided investigation and for testing how the agent uses tools before scheduling it.",
      },
    ],
    markdown: `## What an agent is

An agent is not just a prompt. It is a loop that can plan, call tools, observe results, update context, and continue until it reaches a stopping condition.

In Valpha, agents are strongest for jobs such as "review these symbols and explain which ones deserve deeper analysis", "inspect recent runs and summarize failure patterns", or "retrieve the relevant service instruction before helping a user build a system".

## Tool design

Tools are permissions. If an agent can call a market-data tool, it can inspect market context. If it can call a service-instruction tool, it can retrieve platform guidance. If it can call execution-related tools, the surrounding approvals and risk controls become critical.

Give agents the smallest tool set that completes the job. Add validators for high-impact outputs: trade proposals, account changes, schedule changes, and any recommendation that could lead to live risk.

## Memory

Same-run memory helps the agent avoid repeating work during one task. Durable memory should store stable user preferences, validated lessons, or recurring operational facts. Avoid promoting unverified market opinions or one-off tool results into durable memory.`,
  },
  {
    id: "trading-strategies",
    title: "Trading Concepts and Strategy Patterns",
    eyebrow: "Trading",
    serviceKey: "valpha_tutorial_trading_strategy_risk",
    category: "trading",
    summary:
      "Learn the trading concepts an AI workflow must respect: setup type, regime, risk, sizing, execution, backtesting, and live monitoring.",
    level: "Core",
    estimatedTime: "50 min",
    takeaways: [
      "Classify the strategy before automating it: trend, mean reversion, breakout, event-driven, arbitrage, or portfolio/risk workflow.",
      "Backtest the decision logic separately from live execution assumptions.",
      "Use risk gates for position size, max loss, correlation, account health, and event risk.",
    ],
    appLinks: [
      { label: "Screener", href: "/screener" },
      { label: "Market Overview", href: "/market" },
      { label: "Trade Approvals", href: "/approvals" },
    ],
    documentLinks: [
      { label: "Trading concepts", href: doc("13-trading-concepts-for-ai-systems.md") },
      { label: "Run, automate, backtest", href: doc("05-run-automate-backtest.md") },
      { label: "Safety", href: doc("11-billing-limits-safety.md") },
      { label: "Trading/risk masterclass", href: doc("24-trading-strategy-risk-automation-masterclass.md") },
    ],
    images: [
      {
        src: screenshot("10-ai-system-manual-run.png"),
        alt: "Manual AI system run",
        caption: "Manual runs are the right first step before scheduling or connecting outputs to execution.",
      },
      {
        src: screenshot("11-ai-system-backtesting.png"),
        alt: "AI system backtesting",
        caption: "Backtesting checks historical behavior, but still needs realistic assumptions and out-of-sample review.",
      },
      {
        src: screenshot("13-ai-system-activity-trades.png"),
        alt: "AI system trade activity",
        caption: "Trade activity should be reviewed as a decision trail, not just a performance number.",
      },
    ],
    markdown: `## Strategy families

Trend following tries to participate when price continues in a direction. Mean reversion tries to capture moves back toward a reference value. Breakout strategies look for expansion through a level. Event-driven strategies react to catalysts such as earnings, macro releases, news, or positioning changes. Portfolio workflows manage exposure, allocation, and risk rather than single entries.

## What AI should and should not decide

AI can help classify context, compare evidence, write a structured decision, flag missing information, and explain why a setup is weak or strong.

AI should not bypass sizing, account, broker, approval, or risk constraints. Live execution requires deterministic guardrails even when the AI reasoning is strong.

## Validation path

Start with a paper workflow. Then run historical tests. Then run forward tests with no live order placement. Then require approval before execution. Only after repeated review should any part of the workflow become more automatic.`,
  },
  {
    id: "operations",
    title: "Operations, Monitoring, and Model Lab",
    eyebrow: "Operations",
    serviceKey: "valpha_tutorial_operations_model_lab",
    category: "analysis",
    summary:
      "Use analytics, validation, billing controls, storage, and Model Lab to keep AI automation measurable and maintainable.",
    level: "Advanced",
    estimatedTime: "45 min",
    takeaways: [
      "Treat activity, reliability, and cost analytics as operating controls.",
      "Use shared storage for reusable context and Model Lab for datasets, features, experiments, and deployed models.",
      "Review failures and stale executions before trusting automation schedules.",
    ],
    appLinks: [
      { label: "AI Analytics", href: "/ai/analytics" },
      { label: "Global Storage", href: "/ai/global-storage" },
      { label: "Model Lab", href: "/ai/ml" },
    ],
    documentLinks: [
      { label: "Activity and troubleshooting", href: doc("06-activity-results-troubleshooting.md") },
      { label: "Model Lab for AI systems", href: doc("10-model-lab-for-ai-systems.md") },
      { label: "Capstone labs", href: doc("20-capstone-labs-and-assessments.md") },
      { label: "Templates and review checklists", href: doc("26-templates-schemas-and-review-checklists.md") },
    ],
    images: [
      {
        src: screenshot("14-ai-system-activity-insights.png"),
        alt: "AI system activity insights",
        caption: "Activity insights help you inspect decisions, errors, latency, and result quality.",
      },
      {
        src: screenshot("32-ai-analytics-runs.png"),
        alt: "AI analytics runs",
        caption: "Run analytics show throughput and failure patterns across automation.",
      },
      {
        src: screenshot("36-model-lab-hub.png"),
        alt: "Model Lab hub",
        caption: "Model Lab supports datasets, feature engineering, experiments, training jobs, and model deployment.",
      },
      {
        src: screenshot("44-billing.png"),
        alt: "Billing screen",
        caption: "Billing and limits matter because AI automation consumes balance and should fail before unsafe partial work.",
      },
    ],
    markdown: `## Operating controls

An AI workflow is not finished when the prompt works once. It needs monitoring: run history, failure reason, latency, cost, model used, input freshness, output schema validity, and downstream action review.

Use analytics to identify recurring failures and expensive workflows. Use shared storage for durable context such as system policies, symbol notes, risk rules, and expert playbooks. Use Model Lab when you need structured datasets, reusable features, experiments, training jobs, or deployed models that can support AI systems.

## Maintenance loop

Review recent executions, inspect invalid outputs, check whether inputs were stale or missing, update prompts or schemas, rerun backtests, and document the change. For scheduled automation, confirm that the schedule, account, budget, and risk rules still match the strategy.`,
  },
];

export const AI_SYSTEM_HELP_SECTIONS: HelpSection[] = [
  {
    id: "system-loop",
    title: "System Loop",
    summary: "How a Valpha AI system moves from context to expert reasoning, schema, validation, and action review.",
    image: {
      src: screenshot("08-ai-system-builder.png"),
      alt: "AI system builder",
      caption: "The AI system builder is the control surface for the whole decision workflow.",
    },
    appLinks: [
      { label: "Open AI Systems", href: "/ai/systems" },
      { label: "Deep Tutorial", href: "/tutorial" },
    ],
    lessons: [
      {
        title: "Start from one decision",
        body: "A system should answer one repeatable trading question, such as whether a setup is valid or whether a schedule should produce an advisory signal.",
      },
      {
        title: "Make inputs explicit",
        body: "Price, indicators, storage, snippets, and manual fields should be named dependencies with freshness expectations and failure behavior.",
      },
      {
        title: "Use schema as the contract",
        body: "Structured outputs make the decision auditable and let validators, schedules, approvals, and downstream automations consume the result safely.",
      },
    ],
    checklist: [
      "The system purpose is narrow enough to test.",
      "Every input has a source, timeframe, and fallback rule.",
      "The output schema contains decision, confidence, evidence, risk notes, and action permission fields.",
    ],
  },
  {
    id: "experts",
    title: "Experts",
    summary: "How to choose prompt, code, evaluator, and loop experts for different jobs.",
    image: {
      src: screenshot("45-expert-editor-basics-model-prompts.png"),
      alt: "Expert editor",
      caption: "Experts are specialized reasoning or deterministic units inside an AI system.",
    },
    appLinks: [
      { label: "Expert Library", href: "/ai/experts" },
      { label: "Prompt Snippets", href: "/ai/snippets" },
    ],
    lessons: [
      {
        title: "Prompt experts reason",
        body: "Use them for interpretation, comparison, narrative context, setup scoring, or structured decision writing.",
      },
      {
        title: "Code experts enforce rules",
        body: "Use them for calculations, transformations, thresholds, and checks that should not depend on model judgment.",
      },
      {
        title: "Evaluators compare",
        body: "Use loop or condition evaluators when you need repeated checks, candidate comparison, or pass/fail gates.",
      },
    ],
    checklist: [
      "The expert type matches the job.",
      "The model is selected for required reasoning depth and cost.",
      "The prompt references named inputs and schema fields directly.",
    ],
  },
  {
    id: "inputs-schema-cache",
    title: "Inputs, Schema, Cache",
    summary: "Typed expert inputs, output schema design, and cache policy for reliable reuse.",
    image: {
      src: screenshot("52-expert-cache-output-schema-fields.png"),
      alt: "Expert cache and schema",
      caption: "Cache and schema settings control reuse, cost, and downstream reliability.",
    },
    appLinks: [
      { label: "Indicator Presets", href: "/ai/indicator-sets" },
      { label: "Global Storage", href: "/ai/global-storage" },
    ],
    lessons: [
      {
        title: "Typed inputs prevent ambiguity",
        body: "A price input is not the same as an indicator input, storage input, or prompt snippet. Type them so the system can validate and render them correctly.",
      },
      {
        title: "Schema beats free text",
        body: "Use fields that reflect the actual trading decision: decision, confidence, invalidation, setup type, risk notes, and execution permission.",
      },
      {
        title: "Cache stable context",
        body: "Cache reusable analysis and static policy interpretation. Do not cache live prices, balances, or action-critical state without a freshness gate.",
      },
    ],
    checklist: [
      "Price and indicator inputs include timeframe and lookback.",
      "Schema fields have clear names and expected types.",
      "Cache duration is shorter than the context's useful life.",
    ],
  },
  {
    id: "agents",
    title: "Agents",
    summary: "Where autonomous agents fit beside AI systems and how tools, memory, and validators change behavior.",
    image: {
      src: screenshot("22-agent-tools.png"),
      alt: "Agent tools",
      caption: "Agent tools are the controlled capabilities an agent may use during its loop.",
    },
    appLinks: [
      { label: "Agents", href: "/ai/agents" },
      { label: "Agent Memory", href: "/ai/agent-memory" },
    ],
    lessons: [
      {
        title: "Agents are operators",
        body: "Use an agent when the task needs exploration, tool selection, memory, and multi-step planning rather than a fixed workflow.",
      },
      {
        title: "Tools are permissions",
        body: "The agent can only inspect or change what its tools allow. Keep trading-impacting tools narrow and guarded.",
      },
      {
        title: "Memory needs curation",
        body: "Durable memory should store stable preferences and validated lessons, not every temporary market observation.",
      },
    ],
    checklist: [
      "The agent has only the tools required for the role.",
      "Validators review trade-impacting outputs.",
      "Memory promotion rules are explicit.",
    ],
  },
  {
    id: "trading",
    title: "Trading Use",
    summary: "How to connect AI decisions to trading strategy, risk, testing, and automation readiness.",
    image: {
      src: screenshot("11-ai-system-backtesting.png"),
      alt: "AI system backtesting",
      caption: "Backtests are part of validation, not a replacement for live risk gates.",
    },
    appLinks: [
      { label: "Backtesting", href: "/ai/systems" },
      { label: "Trade Approvals", href: "/approvals" },
    ],
    lessons: [
      {
        title: "Define the strategy family",
        body: "Trend, mean reversion, breakout, event-driven, and portfolio workflows need different inputs and validation.",
      },
      {
        title: "Separate advice from execution",
        body: "A strong AI explanation should still pass account, sizing, max-loss, event, and approval checks before live action.",
      },
      {
        title: "Validate in stages",
        body: "Manual run, historical test, forward test, approval-required automation, then tightly constrained live automation.",
      },
    ],
    checklist: [
      "Strategy assumptions are written down.",
      "Backtest and forward-test results are reviewed.",
      "Risk limits and approvals are active before live execution.",
    ],
  },
];

export const findTutorialModule = (id: string) =>
  TUTORIAL_MODULES.find((module) => module.id === id) ?? TUTORIAL_MODULES[0];
